import { AxiosResponse } from 'axios';
import { TFunction } from 'i18next';
import { RichText } from 'prismic-reactjs';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { getRichTextAsText, getRichTextRendered } from '../../shared/helpers/getRichTextAsText';
import { APIService } from '../../shared/services/APIService';
import { Submission } from '../../user/models/Submission';
import { SubmissionDTO } from '../dto/SubmissionDTO'
import { SubmissionSubmitDTO } from '../dto/SubmissionSubmitDTO';
import { Form } from '../models/Form';

export class FormService extends APIService {

  /**
   * Get form
   */
  getForm(name: string) {
    return this.http.get<Form>('/forms/' + name);
  }

  /**
   * Submit the form responses
   */
  async submitForm(formName: string, dto: SubmissionDTO) {
    return this.http.post<SubmissionSubmitDTO>(`/forms/${ formName }/submissions`, dto)
      .then((response: AxiosResponse<SubmissionSubmitDTO>) => {
        return response.data;
      });
  }

  async updateSubmission(formName: string, submission: SubmissionDTO) : Promise<Submission> {
    return this.http.put<Submission>(`/forms/${formName}/submissions`, submission)
      .then((response: AxiosResponse<Submission>) => {
        return response.data
      })
  }

  async sendPDF(formName: string, pdf: any) {
    const formData = new FormData();
    formData.append('pdf', pdf)
    return this.http.post<any>(`forms/${ formName }/pdf`, formData);
  }

  async sendXLSX(formName: string, xlsx: any) {
    return this.http.post<any>(`forms/${ formName }/xlsx`, xlsx);
  }

  checkIfValue(value: any, key: string) {
    return (value) ? value : key;
  }

  t(t: TFunction, key: string, asRichText: boolean, isUI: boolean = false, asStaticMarkup: boolean = false, isURL: boolean = false): any {
    if (isUI) {
      const isUITranslation = t(key)
      if (isUITranslation.includes('returned an object instead of string')) {
        const fallback = t(key, { returnObjects: true }) as any;

        if (Array.isArray(fallback) && fallback.length > 0 && typeof fallback[0].text === 'string') {
          return fallback[0].text;
        }
      }
      return isUITranslation;
    }
    else if (isURL) {
      const translation = t(key, { returnObjects: true }) as any;

      if (translation.hasOwnProperty('image')) {
        return `${process.env.REACT_APP_API_URL}/images/${translation.image}/download`;
      }
      else if (translation.hasOwnProperty('url')) {
        return translation.url;
      }
      else {
        return null;
      }
    }

    if (asRichText) {
      let renderedContent = getRichTextRendered(t, key)
      renderedContent = renderedContent ? renderedContent : key;

      const addStyleToParagraphs = (node: React.ReactNode): React.ReactNode => {
        if (React.isValidElement(node)) {
          if (node.type === 'p') {
            return React.cloneElement(node, { style: { position: 'relative', ...node.props.style } });
          }

          return React.cloneElement(
            node,
            node.props,
            React.Children.map(node.props.children, addStyleToParagraphs)
          );
        }
        return node;
      };

      const resultWithStyles = addStyleToParagraphs(renderedContent);

      return asStaticMarkup
        ? ReactDOMServer.renderToStaticMarkup(resultWithStyles as any)
        : resultWithStyles;
    }
    else {
      const result = getRichTextAsText(t, key);
      return result ? result : key;
    }
  }

  getQuestionsByType(t: string[]) {
    let types = t.join(',')
    return this.http.get<Form>('/forms/questions', { params: { types } });
  }
}
